import { useState, FormEvent } from 'react';
import { useParallaxTransform } from '@/hooks/useParallax';
import SectionTitle from '@/components/base/SectionTitle';
import confetti from 'canvas-confetti';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL || 'https://lmbvmegvafibwodlweeh.supabase.co';
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '';
const EMAIL_FUNCTION_URL = `${SUPABASE_URL}/functions/v1/send-contact-email`;

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());

  const clearInvalid = (name: string) => {
    setInvalidFields((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const messageEl = form.elements.namedItem('message') as HTMLTextAreaElement;
    const message = messageEl?.value || '';
    if (message.length > 500) return;

    const requiredFields = ['nom', 'email', 'message'];
    const newInvalid = new Set<string>();

    requiredFields.forEach((name) => {
      const el = form.elements.namedItem(name) as HTMLInputElement | HTMLTextAreaElement;
      if (!el || el.value.trim() === '') {
        newInvalid.add(name);
      }
    });

    setInvalidFields(newInvalid);

    if (newInvalid.size > 0) {
      setFormError('Merci de remplir tous les champs obligatoires.');
      return;
    }
    setFormError(null);
    setSubmitting(true);

    try {
      const nom = (form.elements.namedItem('nom') as HTMLInputElement)?.value?.trim() || '';
      const email = (form.elements.namedItem('email') as HTMLInputElement)?.value?.trim() || '';

      const response = await fetch(EMAIL_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ nom, email, message }),
      });

      if (!response.ok) {
        let errorMsg = "Erreur lors de l'envoi. Veuillez réessayer.";
        try {
          const errorData = await response.json();
          errorMsg = errorData.error || errorData.message || errorMsg;
        } catch {
          // ignore JSON parse error
        }
        setFormError(errorMsg);
        return;
      }

      const result = await response.json();

      // Si le mail client n'a pas ete envoye, on affiche un avertissement
      if (result.clientEmailSent === false) {
        setSubmitted(true);
        setFormError(
          "Votre demande a bien ete transmise a notre equipe, mais l'email de confirmation n'a pas pu etre envoye. Verifiez vos spams ou contactez-nous directement."
        );
        return;
      }

      setSubmitted(true);
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#00A8B5', '#FFD700', '#FF6B6B', '#4ECDC4', '#96CEB4'],
      });
    } catch {
      setFormError("Impossible d'envoyer le message. Veuillez reessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  const contentRef = useParallaxTransform(0.04);

  return (
    <section id="contact" className="py-20 sm:py-28 lg:py-16">
      <div ref={contentRef} className="will-change-transform">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          label="Nous Contacter"
          title="Contact"
          delay={100}
        />
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 animate-fade-in-up delay-300">
          {/* Info */}
          <div className="animate-fade-in-up delay-400">
            <h3 className="text-2xl font-semibold text-[#00A8B5] mb-8">Informations</h3>
            <div className="space-y-8 lg:space-y-10 mb-10">
              <div className="flex items-start space-x-5">
                <div className="w-12 h-12 flex items-center justify-center bg-[#00A8B5]/10 rounded-lg flex-shrink-0">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-phone-line text-[#00A8B5] text-xl"></i>
                  </div>
                </div>
                <div>
                  <p className="text-gray-800 text-lg font-semibold">Lou Matthey</p>
                  <a href="tel:+41793275134" className="text-gray-500 text-sm font-light hover:text-[#00A8B5] transition-colors">+41 79 327 51 34</a>
                </div>
              </div>

              <div className="flex items-start space-x-5">
                <div className="w-12 h-12 flex items-center justify-center bg-[#00A8B5]/10 rounded-lg flex-shrink-0">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-phone-line text-[#00A8B5] text-xl"></i>
                  </div>
                </div>
                <div>
                  <p className="text-gray-800 text-lg font-semibold">Lionel Mertes</p>
                  <a href="tel:+41797612277" className="text-gray-500 text-sm font-light hover:text-[#00A8B5] transition-colors">+41 79 761 22 77</a>
                </div>
              </div>

              <div className="flex items-start space-x-5">
                <div className="w-12 h-12 flex items-center justify-center bg-[#00A8B5]/10 rounded-lg flex-shrink-0">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-map-pin-line text-[#00A8B5] text-xl"></i>
                  </div>
                </div>
                <div>
                  <p className="text-gray-800 font-semibold">Vevey, Suisse</p>
                  <p className="text-gray-600">Rue de l'Hôtel-de-Ville 2</p>
                </div>
              </div>
            </div>

            {/* Carte Google Maps */}
            <div className="rounded-xl overflow-hidden border border-gray-200">
              <iframe
                title="Localisation PLUG and BAR LM"
                src="https://maps.google.com/maps?q=Rue+de+l%27H%C3%B4tel-de-Ville+2%2C+1800+Vevey%2C+Suisse&t=&z=15&ie=UTF8&iwloc=&output=embed"
                className="w-full h-64 sm:h-72 border-0"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <div className="animate-fade-in-up delay-500">
            <h3 id="contact-form-title" className="text-2xl font-semibold text-[#00A8B5] mb-8">Formulaire de Contact</h3>
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-16 text-center animate-fade-in">
                <div className="w-16 h-16 flex items-center justify-center bg-[#00A8B5]/20 rounded-full mb-4 animate-bounce">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-check-line text-[#00A8B5] text-3xl"></i>
                  </div>
                </div>
                <h4 className="text-xl font-semibold text-gray-800 mb-2">Message envoye !</h4>
                <p className="text-gray-600">Nous vous repondrons dans les plus brefs delais.</p>
                {formError && (
                  <div className="mt-4 flex items-start gap-2 text-amber-700 text-sm bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-left max-w-md">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-error-warning-line text-amber-600 text-lg"></i>
                    </div>
                    <p>{formError}</p>
                  </div>
                )}
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form="contact-plugandbar"
                className="space-y-5 lg:space-y-6"
                onSubmit={handleSubmit}
              >
                <div id="contact-form-start">
                  <label className="block text-xs text-gray-500 mb-1.5">Nom / Prenom <span className="text-red-500">*</span></label>
                  <input
                    name="nom"
                    placeholder="Votre nom"
                    className={`w-full bg-white border rounded-lg px-5 py-4 text-gray-900 placeholder-gray-500 focus:border-[#00A8B5] focus:outline-none text-sm transition-shadow ${invalidFields.has('nom') ? 'ring-1 ring-[#D64545] border-[#D64545]' : 'border-gray-300'}`}
                    onChange={() => clearInvalid('nom')}
                  />
                  {invalidFields.has('nom') && <p className="text-xs text-[#D64545] mt-1">Champ obligatoire</p>}
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Email <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Votre email"
                    className={`w-full bg-white border rounded-lg px-5 py-4 text-gray-900 placeholder-gray-500 focus:border-[#00A8B5] focus:outline-none text-sm transition-shadow ${invalidFields.has('email') ? 'ring-1 ring-[#D64545] border-[#D64545]' : 'border-gray-300'}`}
                    onChange={() => clearInvalid('email')}
                  />
                  {invalidFields.has('email') && <p className="text-xs text-[#D64545] mt-1">Champ obligatoire</p>}
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Message <span className="text-red-500">*</span></label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="Ecrivez votre message ici..."
                    className={`w-full bg-white border rounded-lg px-5 py-4 text-gray-900 placeholder-gray-500 focus:border-[#00A8B5] focus:outline-none resize-none text-sm transition-shadow ${invalidFields.has('message') ? 'ring-1 ring-[#D64545] border-[#D64545]' : 'border-gray-300'}`}
                    onChange={(e) => { setCharCount(e.target.value.length); clearInvalid('message'); }}
                  ></textarea>
                  {invalidFields.has('message') && <p className="text-xs text-[#D64545] mt-1">Champ obligatoire</p>}
                  <p className="text-right text-xs text-gray-500 mt-1">{charCount}/500</p>
                </div>

                {formError && (
                  <div className="flex items-center justify-center gap-2 text-[#D64545] text-sm text-center bg-[#D64545]/10 border border-[#D64545]/30 rounded-lg px-4 py-3 font-semibold">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line text-[#D64545] text-lg"></i>
                    </div>
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#00A8B5] hover:bg-[#008f9b] text-black font-semibold py-4 rounded-md transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {submitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
      </div>
    </section>
  );
}